import { Space } from "@mantine/core";
import { FunctionComponent, PropsWithChildren } from "react";
import { ScrollRestoration } from "react-router-dom";

import { colors } from "#app/theme/v1/colors";
import { fontSizes } from "#app/theme/v1/fontSizes";
import { radius } from "#app/theme/v1/radius";
import { spacing } from "#app/theme/v1/spacing";
import { IconName } from "#shared/icons/Icon";
import { Box } from "#shared/view/components/Box";
import { Text } from "#shared/view/components/Text";
import { TitleWithIcon } from "#shared/view/components/TitleWithIcon";
import { Breadcrumbs } from "#shared/view/designSystem/Breadcrumbs";

type Breadcrumb = { label: string; href?: string };

type Props = PropsWithChildren<{
  withBorder?: boolean;
  title?: string;
  titleIcon?: IconName;
  subtitle?: string;
  breadcrumbs?: Breadcrumb[];
  fullWidth?: boolean;
}>;

export const PageLayout: FunctionComponent<Props> = ({
  children,
  withBorder,
  title,
  titleIcon,
  subtitle,
  breadcrumbs,
  fullWidth,
}) => {
  return (
    <Box style={styles(withBorder, fullWidth)}>
      <ScrollRestoration />
      {breadcrumbs && (
        <>
          <Breadcrumbs
            breadcrumbs={breadcrumbs.map((breadcrumb) => ({
              ...breadcrumb,
              href: breadcrumb.href ?? "#",
            }))}
          />
          <Space h="sm" />
        </>
      )}
      {title && <TitleWithIcon title={title} iconName={titleIcon} />}
      {subtitle && (
        <>
          <Space h="xxs" />
          <Text fz={fontSizes.medium}>{subtitle}</Text>
        </>
      )}
      {children}
    </Box>
  );
};

const styles = (withBorder: boolean | undefined, fullWidth: boolean | undefined) => ({
  width: fullWidth ? "100%" : "auto",
  padding: withBorder ? spacing.lg : 0,
  gap: spacing.lg,
  borderRadius: radius.sm,
  border: withBorder ? `1px solid ${colors.gray[3]}` : undefined,
  marginTop: spacing.sm,
  marginBottom: spacing.sm,
  marginLeft: spacing.xs,
  marginRight: spacing.xs,
});
